import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["recordButton", "stopButton", "recordingStatus", "recordingTime"]
  static values = {
    chatroomId: String,
    audioUrl: String
  }

  connect() {
    this.isRecording = false
    this.mediaRecorder = null
    this.audioChunks = []
    this.recordingStartTime = null
    this.recordingTimer = null
    this.audioBlob = null
  }

  disconnect() {
    this.stopRecording()
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
    }
  }

  startRecording(event) {
    event.preventDefault()

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          // Try different audio formats in order of WhatsApp compatibility
          // Note: We can't use audio/ogg with MediaRecorder as it produces incompatible files
          // So we'll use webm and convert on backend
          let options = { mimeType: 'audio/webm; codecs=opus' }
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            options = { mimeType: 'audio/webm' }
          }

          this.mediaRecorder = new MediaRecorder(stream, options)
          this.audioChunks = []
          this.recordedMimeType = options.mimeType

          this.mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              this.audioChunks.push(event.data)
            }
          }

          this.mediaRecorder.onstop = () => {
            // Use the actual mime type that was recorded
            const mimeType = this.recordedMimeType || 'audio/webm'
            this.audioBlob = new Blob(this.audioChunks, { type: mimeType })
            this.stopAllTracks(stream)

            // Automatically show the audio in textarea after recording stops
            this.showAudioInTextarea()
          }

          this.mediaRecorder.start()
          this.isRecording = true
          this.recordingStartTime = Date.now()

          this.updateUI()
          this.startRecordingTimer()
        })
        .catch(error => {
          console.error('Error accessing microphone:', error)
          alert('No se pudo acceder al micrófono. Por favor, verifica los permisos.')
        })
    } else {
      alert('Tu navegador no soporta grabación de audio.')
    }
  }

  stopRecording(event) {
    if (event) {
      event.preventDefault()
    }

    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop()
      this.isRecording = false

      if (this.recordingTimer) {
        clearInterval(this.recordingTimer)
        this.recordingTimer = null
      }

      this.updateUI()
    }
  }


  showAudioInTextarea() {
    // Find the message-type controller
    const messageTypeController = this.element.closest('[data-controller*="message-type"]')
    if (!messageTypeController) {
      console.error('Message type controller not found')
      return
    }

    const messageTypeInstance = this.application.getControllerForElementAndIdentifier(messageTypeController, 'message-type')
    if (!messageTypeInstance) {
      console.error('Message type instance not found')
      return
    }

    // Always use .webm extension for the file
    const mimeType = this.audioBlob.type
    const extension = 'webm'

    // Calculate audio duration
    const duration = this.getRecordingDuration()

    // Create a file from the audio blob
    const audioFile = new File([this.audioBlob], `audio_${Date.now()}.${extension}`, { type: mimeType })

    // Set the file input
    const fileInput = messageTypeController.querySelector('input[type="file"]')
    if (fileInput) {
      // Create a new FileList with our audio file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(audioFile)
      fileInput.files = dataTransfer.files

      // Update the message type to audio
      messageTypeInstance.currentType = 'audio'
      messageTypeInstance.typeInputTarget.value = 'audio'
      messageTypeInstance.audioDuration = duration // Store duration for preview

      // Add duration as hidden field to the form
      const form = messageTypeController.querySelector('form')
      if (form) {
        // Remove existing duration field if any
        const existingDurationField = form.querySelector('input[name="audio_duration"]')
        if (existingDurationField) {
          existingDurationField.remove()
        }

        // Add new duration field
        const durationField = document.createElement('input')
        durationField.type = 'hidden'
        durationField.name = 'audio_duration'
        durationField.value = duration
        form.appendChild(durationField)
      }

      // Trigger the file change event to show preview
      const changeEvent = new Event('change', { bubbles: true })
      fileInput.dispatchEvent(changeEvent)

      // Call updateForm to refresh the submit button and other UI elements
      messageTypeInstance.updateForm()
    } else {
      console.error('File input not found')
    }

    // Hide the audio recorder and show the textarea
    this.hideAudioRecorder()
  }

  getRecordingDuration() {
    if (!this.recordingStartTime) {
      return '0:00'
    }

    const elapsed = Date.now() - this.recordingStartTime
    const seconds = Math.floor(elapsed / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  cancelRecording(event) {
    event.preventDefault()
    this.resetRecorder()
    this.hideAudioRecorder()

    // Reset to text mode
    const messageTypeController = this.element.closest('[data-controller*="message-type"]')
    if (messageTypeController) {
      const messageTypeInstance = this.application.getControllerForElementAndIdentifier(messageTypeController, 'message-type')
      if (messageTypeInstance) {
        messageTypeInstance.currentType = 'text'
        messageTypeInstance.typeInputTarget.value = 'text'
        messageTypeInstance.updateForm()
      }
    }
  }

  hideAudioRecorder() {
    // Find the message-type controller and hide the audio recorder
    const messageTypeController = this.element.closest('[data-controller*="message-type"]')
    if (messageTypeController) {
      const messageTypeInstance = this.application.getControllerForElementAndIdentifier(messageTypeController, 'message-type')
      if (messageTypeInstance && messageTypeInstance.hideAudioRecorder) {
        messageTypeInstance.hideAudioRecorder()
      }
    }
  }

  resetRecorder() {
    if (this.isRecording) {
      this.stopRecording({ preventDefault: () => {} })
    }
    this.audioBlob = null
    this.audioChunks = []
    this.recordingStartTime = null

    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
      this.recordingTimer = null
    }

    // Reset recording time display
    if (this.hasRecordingTimeTarget) {
      this.recordingTimeTarget.textContent = '00:00'
    }

    this.updateUI()
  }

  startRecordingTimer() {
    this.recordingTimer = setInterval(() => {
      if (this.recordingStartTime) {
        const elapsed = Date.now() - this.recordingStartTime
        const seconds = Math.floor(elapsed / 1000)
        const minutes = Math.floor(seconds / 60)
        const remainingSeconds = seconds % 60

        const timeString = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
        this.recordingTimeTarget.textContent = timeString
      }
    }, 1000)
  }

  updateUI() {
    if (this.isRecording) {
      this.recordButtonTarget.style.display = 'none'
      this.stopButtonTarget.style.display = 'inline-block'
      this.recordingStatusTarget.textContent = 'Grabando...'
      this.recordingStatusTarget.style.color = '#dc3545'
    } else {
      this.recordButtonTarget.style.display = 'inline-block'
      this.stopButtonTarget.style.display = 'none'
      this.recordingStatusTarget.textContent = 'Presiona para grabar'
      this.recordingStatusTarget.style.color = '#6c757d'
    }
  }


  stopAllTracks(stream) {
    stream.getTracks().forEach(track => track.stop())
  }
}
